import { AfterContentChecked, Component, ElementRef, HostListener, Inject, OnInit, ViewChild } from "@angular/core";
import { MatDialog, MatDialogRef, MAT_DIALOG_DATA as MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar as MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { Select, Store } from "@ngxs/store";
import { PatientEntity } from "huddyapi";
import { PatientService } from "huddyapi";
import { BulkSubscription, BulkSubscriptionAssignment, BulkSubscriptionAssignmentAssessmentTaker, BulkSubscriptionAssignments, BulkSubscriptionAssignmentStatus, Compass, CompassScaleRange, GetBulkSubscriptionAssignments, GetCompassScoresByCompass, Pair, SaveBulkSubscriptionAssignment, ScoredSubscription, ScoreReportingLevel } from "huddyapi";
import { Observable, timer } from "rxjs";
import { DesktopErrorHandler } from "../../services/errorHandler.service";
import { CountUpdators, MapCountDependency, SavedField, SavedGroupStatus, SavedObject, SavedSelectField, SavedTextField, SelectOptions, SimpleSavedField, SimpleStatusField } from "../multisave";
import { Util } from "../util";

type SavedAssignments = { [id: number]: SavedAssignment[] };

@Component({
  selector: 'compass-library-manage-dialog',
  templateUrl: 'compass-library-manage-dialog.component.html',
  styleUrls: ["./compass-library-manage-dialog.component.scss"]
})
export class CompassLibraryManageDialog implements OnInit, AfterContentChecked {
  @Select(state => state.compassState.assignments) assignments$: Observable<BulkSubscriptionAssignments>;
  @Select(state => state.compassState.scores) scores$: Observable<{ [compassId: number]: { [userId: number]: ScoredSubscription[] } }>;

  @ViewChild('fieldInfo', { static: false }) fieldInfo: ElementRef;
  @ViewChild('filter', { static: false }) set filterInput(filterInput: ElementRef) {
    this._filterInput = filterInput;
    this.focusFilter();
  }

  get filterInput(): ElementRef {
    return this._filterInput;
  }

  set filterText(filterText: string) {
    if(filterText == null)
      filterText = '';
    this._filterText = filterText;
    this.normalizedFilterText = this.normalizeName(filterText);
    this.applyFilter();
  }

  get filterText(): string {
    return this._filterText;
  }

  set editing(editing: boolean) {
    this._editing = editing;
    this.dialogRef.disableClose = this.editing;
  }

  get editing(): boolean {
    return this._editing;
  }

  set version(version: number) {
    this._version = version;
    this.focusFilter();
    this.autoSelect();
  }

  get version(): number {
    return this._version;
  }

  _version: number;
  selected: SavedAssignment;
  _editing: boolean;
  _filterInput: ElementRef;
  _filterText: string;
  normalizedFilterText: string;
  loadingAssignments: boolean;
  loadingScores: { [compassId: number]: boolean };
  assignmentMap: Map<number, SavedAssignment>;
  savedAssignments: SavedAssignments;
  filteredAssignments: SavedAssignments;
  emptyAssignments: SavedAssignment[];
  assignedCount: number;
  emailDependency: MapCountDependency<string>;
  scores: { [compassId: number]: { [userId: number]: ScoredSubscription[] } };
  compassMap: { [compassId: number]: Compass };
  indexToSelect: number;
  shouldScrollToNew: boolean;
  fieldInfoShowing: boolean;
  fieldInfoElem: HTMLElement;
  fieldInfoPosition: string;
  fieldInfoArrowPosition: string;

  constructor(
    public dialogRef: MatDialogRef<CompassLibraryManageDialog>,
    public snackbar: MatSnackBar,
    public store: Store,
    public patientService : PatientService,
    public dialog: MatDialog,
    public router: Router,
    public errorHandler: DesktopErrorHandler,
    @Inject(MAT_DIALOG_DATA) public data: CompassLibraryManageDialogData) {
    this.editing = false;
    this.shouldScrollToNew = false;
    this.indexToSelect = -1;
    this.fieldInfoShowing = false;
    this.loadingAssignments = true;
    this.loadingScores = {};
    data.compasses.forEach((compass: Compass) => this.loadingScores[compass.id] = true);
    this.scores = {};
    this.compassMap = {};
    this.savedAssignments = {};
    this.emptyAssignments = [];
    this.assignedCount = 0;
    this.emailDependency = new MapCountDependency<string>();
    this.assignmentMap = new Map<number, SavedAssignment>();
    this.version = data.compasses[0].id;
    this.filterText = '';
    this.applyFilter();
  }

  ngOnInit() {
    this.loadingAssignments = true;
    this.focusFilter();
    this.loadingScores = {};
    this.data.compasses.forEach((compass: Compass) => this.loadingScores[compass.id] = true);
    this.store.dispatch(new GetBulkSubscriptionAssignments([this.data.subscription.id])).subscribe(() => this.loadingAssignments = false, (error: any) => {
      this.loadingAssignments = false;
      this.errorHandler.handle(error, 'assignments');
    });
    this.data.compasses.forEach((compass: Compass) => {
      this.compassMap[compass.id] = compass;
      this.store.dispatch(new GetCompassScoresByCompass(compass.id)).subscribe(() => {
        this.loadingScores[compass.id] = false;
      }, (error: any) => {
        this.loadingScores[compass.id] = false;
        this.errorHandler.handle(error, (compass.name || 'compass') + ' scores');
      });
    });
    this.store.dispatch(new GetCompassScoresByCompass(this.data.subscription.compass.id))
    this.assignments$.subscribe((assignments: BulkSubscriptionAssignments) => {
      let savedAssignments: SavedAssignments = {}, assignmentMap: Map<number, SavedAssignment> = new Map<number, SavedAssignment>();
      const assignmentList: BulkSubscriptionAssignment[] = assignments[this.data.subscription.id] || [];
      this.handleAssignments(assignmentList, savedAssignments, assignmentMap);
      let assignedCount: number = 0;
      assignmentList.forEach((assignment: BulkSubscriptionAssignment) => {
        if(assignment.subscriptionStatus != BulkSubscriptionAssignmentStatus.UNASSIGNED)
          assignedCount++;
      });
      if(this.selected && !assignmentMap.has(this.selected.id)) {
        if(this.editing && !this.selected.isSaving()) {
          this.selected.updateFrom(null);
          this.addToSavedAssignments(savedAssignments, this.selected);
          assignmentMap.set(this.selected.id, this.selected);
        }
      }
      this.emailDependency.clear(false);
      assignmentList.forEach((assignment: BulkSubscriptionAssignment) => {
        if(assignment.subjectType == 'TRUSTED_USER')
          this.emailDependency.add(assignment.subject, false);
      });
      this.emailDependency.update();
      this.assignedCount = assignedCount;
      this.assignmentMap = assignmentMap;
      this.savedAssignments = savedAssignments;
      this.applyFilter();
    });
    this.scores$.subscribe((scores: { [compassId: number]: { [userId: number]: ScoredSubscription[] } }) => {
      this.scores = scores;
      for(let compassId in this.savedAssignments)
        (this.savedAssignments[compassId] || []).forEach((assignment: SavedAssignment) => this.scoreAssignment(assignment));
    });
  }

  ngAfterContentChecked(): void {
    if(this.fieldInfoShowing && this.fieldInfoElem) {
      this.fieldInfoPosition = this.getFieldInfoPosition();
      this.fieldInfoArrowPosition = this.getFieldInfoArrowPosition();
    }
  }

  scrollToNew(element: ElementRef) {
    if(!this.shouldScrollToNew)
      return;
    this.shouldScrollToNew = false;
    timer(0).subscribe(() => {
      element?.nativeElement?.scrollIntoViewIfNeeded();
    });
  }
  
  setShouldScrollToNew(): void {
    this.shouldScrollToNew = true;
  }

  scoreAssignment(assignment: SavedAssignment) {
    assignment.setScores(this.getCompassUserScores(assignment.subjectUserId, this.compassMap[assignment.compassId]));
  }

  getCompassUserScores(userId: number, compass = this.compassMap[this.version]): CompassUserScoreInfo[] {
    if(!compass || !userId)
      return [];
    if((this.loadingScores[compass.id] || {})[userId] == true)
      return [{ score: 'Loading...', assessmentTakers: [] } as CompassUserScoreInfo];
    let map: Map<string, CompassUserScoreInfo> = new Map<string, CompassUserScoreInfo>();
    ((this.scores[compass.id] || {})[userId] || []).forEach((score: ScoredSubscription) => {
      const scoreString: string = this.getCompassScoreString(compass, score.score), assessmentTaker: string = score.assessmentTaker || score.assessmentTakerEmail || '?';
      if(scoreString == 'No score')
        return;
      let info: CompassUserScoreInfo = map.get(scoreString);
      if(!info) {
        info = {
          score: scoreString,
          assessmentTakers: []
        } as CompassUserScoreInfo;
        map.set(scoreString, info);
      }
      info.assessmentTakers.push(assessmentTaker);
    });
    return Array.from(map.values());
  }

  getCompassScoreString(compass: Compass, score: number): string {
    if(score != null) {
      const ranges: CompassScaleRange[] = compass && compass.scoreSummaryGroups ? (compass.scoreSummaryGroups.length == 1 ? compass.scoreSummaryGroups[0].scaleRanges : compass.scoreSummaryRanges) : [];
      if(ranges?.length > 0) {
        for(let range of ranges) {
          if(score >= range.minimum && score <= range.maximum)
            return range.name;
        }
      }
    }
    return 'No score';
  }

  addToSavedAssignments(savedAssignments: SavedAssignments, assignment: SavedAssignment) {
    const arr: SavedAssignment[] = savedAssignments[assignment.compassId];
    if(arr) {
      const index: number = Util.sortedIndexOf(arr, assignment, SavedAssignment.compare);
      if(index < 0)
        arr.splice(index * -1 - 1, 0, assignment);
      else
        arr[index] = assignment;
    } else
      savedAssignments[assignment.compassId] = [assignment];
  }

  removeFromSavedAssignments(savedAssignments: SavedAssignments, id: number, compassId: number) {
    const arr: SavedAssignment[] = savedAssignments[compassId];
    if(arr)
      savedAssignments[compassId] = arr.filter((assignment: SavedAssignment): boolean => assignment.id != id);
  }

  handleAssignments(assignments: BulkSubscriptionAssignment[], savedAssignments: SavedAssignments, assignmentMap: Map<number, SavedAssignment>) {
    assignments.forEach((assignment: BulkSubscriptionAssignment) => {
      if(assignment.createdFrom != null && this.assignmentMap.has(assignment.createdFrom)) {
        let existing: SavedAssignment = this.assignmentMap.get(assignment.createdFrom);
        existing.updateFrom(assignment);
        existing.reset();
        this.addToSavedAssignments(savedAssignments, existing);
        assignmentMap.set(assignment.id, existing);
      } else if(this.assignmentMap.has(assignment.id)) {
        let existing: SavedAssignment = this.assignmentMap.get(assignment.id);
        existing.updateFrom(assignment);
        if(!(this.editing && this.isSelected(existing) && !existing.isSaving()))
          existing.reset(false);
        this.addToSavedAssignments(savedAssignments, existing);
        assignmentMap.set(assignment.id, existing);
      } else {
        const savedAssignment: SavedAssignment = new SavedAssignment(assignment, this);
        this.addToSavedAssignments(savedAssignments, savedAssignment);
        assignmentMap.set(assignment.id, savedAssignment);
      }
    });
  }

  isLoading(): boolean {
    return this.loadingAssignments;
  }

  normalizeName(name: string): string {
    return (name == null ? '' : name).toLowerCase().replace(/\s/g, '');
  }

  getRelevantAssignments(): SavedAssignment[] {
    return (this.filteredAssignments || {})[this.version] || this.emptyAssignments;
  }

  assignmentMatchesFilter(assignment: SavedAssignment): boolean {
    return this.normalizeName(assignment.getListingName()).includes(this.normalizedFilterText)
      || assignment.savedAssessmentTakers.findIndex((assessmentTaker: BulkSubscriptionAssignmentAssessmentTaker): boolean => this.normalizeName(assessmentTaker.email).includes(this.normalizedFilterText)) >= 0;
  }

  applyFilter() {
    let filteredAssignments = {};
    for(let id in this.savedAssignments)
      filteredAssignments[id] = this.normalizedFilterText == '' ? this.savedAssignments[id] : (this.savedAssignments[id] || []).filter((assignment: SavedAssignment): boolean => this.assignmentMatchesFilter(assignment));
    this.filteredAssignments = filteredAssignments;
    this.autoSelect();
  }

  autoSelect() {
    if(this.selected && this.assignmentMap.has(this.selected.id) && this.selected.compassId == this.version && this.assignmentMatchesFilter(this.assignmentMap.get(this.selected.id)))
      return;
    let arr: SavedAssignment[] = this.getRelevantAssignments();
    if(arr.length == 0)
      this.select(null);
    else
      this.select(arr[Math.max(0, Math.min(arr.length - 1, this.indexToSelect))]);
  }

  select(assignment: SavedAssignment) {
    if(this.editing)
      return;
    if(assignment != this.selected) {
      this.indexToSelect = -1;
      this.fieldInfoElem = null;
      this.selected = assignment;
      this.focusFilter();
    }
  }

  isSelected(assignment: SavedAssignment): boolean {
    return this.selected?.id == assignment?.id;
  }

  add() {
    const toAdd: SavedAssignment = new SavedAssignment(null, this);
    toAdd.bulkSubscriptionId = this.data.subscription.id;
    toAdd.compassId = this.version;
    this.assignmentMap.set(toAdd.id, toAdd);
    this.addToSavedAssignments(this.savedAssignments, toAdd);
    this.select(toAdd);
    this.edit();
  }

  edit(which: boolean = true) {
    if(which) {
      this.editing = true;
      return;
    }
    if(this.selected.isNew()) {
      this.selected.delete().then((remove: boolean) => {
        if(!remove)
          return;
        const toDelete: SavedAssignment = this.selected;
        this.removeFromSavedAssignments(this.savedAssignments, toDelete.id, toDelete.compassId);
        this.assignmentMap.delete(toDelete.id);
        this.editing = false;
        this.applyFilter();
        this.autoSelect();
      });
      return;
    }
    if(!this.selected.canReset() && !this.selected.isSaving()) {
      this.editing = false;
      return;
    }
    this.selected.reset(true).then((reset: boolean) => {
      if(reset)
        this.editing = false;
    });
  }

  canViewResults(assignment: SavedAssignment): boolean {
    return assignment.subjectUserId != null;
  }

  viewResults(assignment: SavedAssignment) {
    if(!this.canViewResults(assignment))
      return;
    this.patientService.getPatientByUser(null, assignment.subjectUserId).subscribe((patient: PatientEntity) => {
      this.router.navigateByUrl('/patient/' + patient.id).then((succeeded: boolean) => {
        if(succeeded)
          this.dialogRef.close();
        else
          this.errorHandler.handleSimple(null, 'Navigation failed');
      }).catch((error: any) => {
        this.errorHandler.handleSimple(error, 'Navigation failed');
      });
    });
  }

  canAssign(): boolean {
    return this.assignedCount < this.data.subscription.quantity;
  }

  assign(which: boolean = true) {
    if(which && !this.canAssign())
      return;
    this.selected.subscriptionStatus.value = which ? BulkSubscriptionAssignmentStatus.PENDING : BulkSubscriptionAssignmentStatus.UNASSIGNED;
    this.save(this.selected, false);
  }

  save(assignment: SavedAssignment, shouldExit: boolean = true) {
    assignment.save().then((saved: boolean) => {
      if(shouldExit && saved) {
        this.editing = false;
        this.autoSelect();
      }
    });
  }

  focusFilter() {
    if(this.filterInput != null)
      this.filterInput.nativeElement.focus();
  }

  getEmptyStateTitle(): string {
    if(((this.savedAssignments || {})[this.version] || []).length == 0)
      return 'No assignments';
    return 'No assignments matching your filter';
  }

  getEmptyStateDesc(): string {
    if(((this.savedAssignments || {})[this.version] || []).length == 0)
      return 'Create a new assignment';
    return 'Change the filter text or create a new assignment';
  }

  getSelectedIndex(): number {
    let result: number = -1;
    if(this.selected)
      result = this.getRelevantAssignments().findIndex((assignment: SavedAssignment): boolean => this.isSelected(assignment));
    return result;
  }

  selectNext(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    const arr: SavedAssignment[] = this.getRelevantAssignments();
    this.select(arr[(index + 1) % arr.length]);
  }

  selectPrevious(): void {
    const index = this.getSelectedIndex();
    if(index < 0)
      return;
    const arr: SavedAssignment[] = this.getRelevantAssignments();
    this.select(arr[index == 0 ? arr.length - 1 : index - 1]);
  }

  getFieldInfoPositionInfo(): { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } {
    if(!(this.fieldInfoElem && this.fieldInfo && this.fieldInfo.nativeElement))
      return null;
    let vMode: number = 0; //-1 = above, 0 = side, 1 = below
    const position = this.fieldInfoElem.getBoundingClientRect();
    if(position.y <= 17)
      vMode = 1;
    else if(position.bottom + 17 >= window.innerHeight)
      vMode = -1;
    const popoverSpace: number = this.fieldInfo.nativeElement.offsetWidth + 20;
    let leftSpace: boolean = position.left - popoverSpace > 0, rightSpace: boolean = position.right + popoverSpace < window.innerWidth;
    if(vMode == 0 && !(leftSpace || rightSpace))
      vMode = (position.top + position.height / 2 > window.innerHeight / 2) ? -1 : 1;
    return { position: position, vMode: vMode, leftSpace: leftSpace, rightSpace: rightSpace };
  }

  getFieldInfoArrowPosition(): string {
    let top: string = '0', left: string = '0', transform: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      switch(positionInfo.vMode) {
        case -1:
          top = (positionInfo.position.top - 11).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          transform = 'scaleY(-1)';
          break;
        case 0:
          top = (positionInfo.position.top + positionInfo.position.height / 2 - 4).toString() + 'px';
          if(positionInfo.rightSpace) {
            left = (positionInfo.position.right - 1).toString() + 'px';
            transform = 'rotate(-90deg)';
          } else {
            left = (positionInfo.position.left - 17).toString() + 'px';
            transform = 'rotate(90deg)';
          }
          break;
        case 1:
          top = (positionInfo.position.bottom + 4).toString() + 'px';
          left = (positionInfo.position.left + positionInfo.position.width / 2 - 9.5).toString() + 'px';
          break;
      }
    }
    return 'top:' + top + ';left:' + left + ';transform:' + transform + ';';
  }

  getFieldInfoPosition(): string {
    let top: string = '0', right: string = 'unset', bottom: string = 'unset', left: string = '0', maxHeight: string = 'unset';
    const positionInfo: { position: DOMRect, vMode: number, leftSpace: boolean, rightSpace: boolean } = this.getFieldInfoPositionInfo();
    if(positionInfo) {
      if(positionInfo.vMode == 0) {
        maxHeight = (window.innerHeight - 20).toString() + 'px';
        const normalTop: number = positionInfo.position.top + positionInfo.position.height / 2 - 22;
        top = Math.min(Math.max(10, window.innerHeight - this.fieldInfo.nativeElement.offsetHeight - 10), normalTop).toString() + 'px';
        if(positionInfo.rightSpace) {
          left = (positionInfo.position.right + 11).toString() + 'px';
        } else {
          left = 'unset';
          right = (window.innerWidth - (positionInfo.position.left - 11)).toString() + 'px';
        }
      } else {
        const normalLeft: number = positionInfo.position.left + positionInfo.position.width / 2 - 22;
        const rightPos: number = positionInfo.position.right - positionInfo.position.width / 2 + 22;
        left = Math.min(Math.max(Math.max(10, rightPos - this.fieldInfo.nativeElement.offsetWidth), window.innerWidth - this.fieldInfo.nativeElement.offsetWidth - 10), normalLeft).toString() + 'px';
        if(positionInfo.vMode == -1) {
          top = 'unset';
          const bottomPos: number = positionInfo.position.top - 11;
          bottom = (window.innerHeight - bottomPos).toString() + 'px';
          maxHeight = (bottomPos - 10).toString() + 'px';
        } else {
          const topNum: number = positionInfo.position.bottom + 11;
          top = topNum.toString() + 'px';
          maxHeight = (window.innerHeight - topNum - 10).toString() + 'px';
        }
      }
    }
    return 'top:' + top + ';right:' + right + ';bottom:' + bottom + ';left:' + left + ';--max-height:' + maxHeight + ';';
  }

  showFieldInfo(event: any, text: string) {
    if(!this.fieldInfo)
      return;
    this.fieldInfo.nativeElement.getElementsByClassName('text')[0].innerHTML = text;
    this.fieldInfoElem = event.target;
    this.fieldInfoShowing = true;
  }

  hideFieldInfo() {
    this.fieldInfoShowing = false;
    this.fieldInfoElem = null;
  }

  ignoreKeyboardArrowEvent(event: any): boolean {
    return event.target.tagName == 'MAT-SELECT' || event.target.tagName == 'TEXTAREA';
  }
   
  @HostListener('window:keydown', ['$event'])
  handleKeydown(event: any): void {
    if(this.dialog.openDialogs?.length > 0 && this.dialog.openDialogs[this.dialog.openDialogs.length - 1] != this.dialogRef)
      return;
    switch(event.key) {
      case 's':
        if((window.navigator.platform.match("Mac") ? event.metaKey : event.ctrlKey)) {
          event.preventDefault();
          if(this.editing && this.selected?.saveEnabled())
            this.save(this.selected);
        }
        break;
      case 'ArrowUp':
        if(!this.ignoreKeyboardArrowEvent(event)) {
          event.preventDefault();
          this.selectPrevious();
        }
        break;
      case 'ArrowDown':
        if(!(this.ignoreKeyboardArrowEvent(event))) {
          event.preventDefault();
          this.selectNext();
        }
        break;
      default:
    }
  }
}

export interface CompassLibraryManageDialogData {
  subscription: BulkSubscription;
  compasses: Compass[];
}

export class SavedAssignment extends SavedObject<BulkSubscriptionAssignment> {
  objectName = 'assignment';
  parent: CompassLibraryManageDialog;
  bulkSubscriptionId: number;
  compassId: number;
  subjectUserId: number;
  subscriptionStatus: SavedField<BulkSubscriptionAssignmentStatus>;
  subjectType: SavedSelectField<string>;
  subject: SavedTextField;
  subjectScoreLevel: SavedSelectField<ScoreReportingLevel>;
  takesOwnAssessment: SavedSelectField<boolean>;
  assessmentTakerEmailDependency: MapCountDependency<string>;
  assessmentTakersGroup: SavedGroupStatus;
  assessmentTakers: BulkSubscriptionAssignmentAssessmentTakerGroup[];
  savedAssessmentTakers: BulkSubscriptionAssignmentAssessmentTaker[];
  statusField: SimpleStatusField;
  scoreNames: string[];
  scoreInfo: Map<string, CompassUserScoreInfo>;

  constructor(from: BulkSubscriptionAssignment, parent: CompassLibraryManageDialog) {
    super(from, false, null, null, parent.dialog);
    this.parent = parent;
    this.scoreNames = [];
    this.scoreInfo = new Map<string, CompassUserScoreInfo>();
    this.bulkSubscriptionId = from ? from.bulkSubscriptionId : null;
    this.compassId = from ? from.compassId : null;
    this.subjectUserId = from ? from.subjectUserId : null;
    this.parent.scoreAssignment(this);
    this.assessmentTakerEmailDependency = new MapCountDependency<string>();
    this.subscriptionStatus = new SimpleSavedField<BulkSubscriptionAssignmentStatus>(from ? from.subscriptionStatus : BulkSubscriptionAssignmentStatus.PENDING, null, this.updators);
    this.assessmentTakersGroup = new SavedGroupStatus(this.updators);
    this.assessmentTakers = [];
    this.savedAssessmentTakers = this.getAssessmentTakers(from);
    this.takesOwnAssessment = new SavedSelectField<boolean>(this.getTakesOwnAssessment(from), null, this.updators, null, null, true, null, null, new SelectOptions([
      { displayValue: 'Yes', value: true },
      { displayValue: 'No', value: false }
    ]));
    this.subjectType = new SavedSelectField<string>(this.getSubjectType(from), null, this.updators, null, null, true, null, null, new SelectOptions<string>([
      { displayValue: 'Independent', value: 'TRUSTED_USER' },
      { displayValue: 'Dependent', value: 'LOG_FOR' }
    ]), null, (oldValue: string, newValue: string, field: SavedSelectField<string>) => {
      if(!this.subject || oldValue == newValue)
        return;
      if(field.value == field.savedValue)
        this.subject.reset();
      else
        this.subject.value = null;
      if(newValue == 'LOG_FOR')
        this.takesOwnAssessment.value = false;
      else if(oldValue == 'LOG_FOR')
        this.takesOwnAssessment.reset();
    });
    this.statusField = new SimpleStatusField(this.assessmentTakersGroup.updators, [this.subjectType, this.takesOwnAssessment], [
      (): string => (this.takesOwnAssessment.value == false || this.subjectType.value == 'LOG_FOR') && this.assessmentTakers.length == 0 ? 'Must provide at least one' : ''
    ], (): boolean =>
      this.assessmentTakers.length != this.savedAssessmentTakers.length || this.assessmentTakers.findIndex((taker: BulkSubscriptionAssignmentAssessmentTakerGroup): boolean => taker.isNew) != -1);
    this.subject = new SavedTextField(this.getSubject(from), null, this.updators, [this.subjectType, parent.emailDependency], [
      (field: SavedTextField): string => this.subjectType.value == 'TRUSTED_USER' && field.value?.length > 0 && !Util.isEmail(field.value) ? 'Invalid email' : '',
      (field: SavedTextField): string => (this.subjectType.value == 'TRUSTED_USER' && parent.emailDependency.count(field.value) > (field.value != field.savedValue ? 0 : 1)) ? 'Duplicate' : ''
    ], null, null, true, null, 128);
    this.subjectScoreLevel = new SavedSelectField<ScoreReportingLevel>(this.getScoreLevel(from), null, this.updators, null, null, true, null, null, new SelectOptions([
      { displayValue: 'None', value: ScoreReportingLevel.NONE },
      { displayValue: 'Summary', value: ScoreReportingLevel.SUMMARY },
      { displayValue: 'Component', value: ScoreReportingLevel.COMPONENT },
      { displayValue: 'Detailed', value: ScoreReportingLevel.DETAILED }
    ]));
  }

  clearAssessmentTakers(shouldUpdate: boolean = true) {
    this.assessmentTakers.forEach((taker: BulkSubscriptionAssignmentAssessmentTakerGroup) => taker.destroy());
    this.assessmentTakers = [];
    if(shouldUpdate)
      this.statusField?.update();
  }

  setAssessmentTakers(assessmentTakers: BulkSubscriptionAssignmentAssessmentTaker[]) {
    if(assessmentTakers?.length > 0) {
      let i = 0;
      const minLength: number = Math.min(assessmentTakers.length, this.assessmentTakers.length);
      while(i < minLength) {
        this.assessmentTakers[i].updateFrom(assessmentTakers[i]);
        i++;
      }
      while(i < assessmentTakers.length) {
        this.addAssessmentTaker(assessmentTakers[i]), false;
        i++;
      }
      while(i < this.assessmentTakers.length)
        this.removeAssessmentTaker(i, false);
    } else
      this.clearAssessmentTakers(false);
    this.statusField?.update();
  }

  getSubject(from: BulkSubscriptionAssignment): string {
    return from ? (from.subjectType == 'LOG_FOR' ? from.subject : from.login) : null;
  }

  getSubjectType(from: BulkSubscriptionAssignment): string {
    const type: string = from?.subjectType || 'TRUSTED_USER';
    return type == 'SELF' ? 'TRUSTED_USER' : type;
  }

  getTakesOwnAssessment(from: BulkSubscriptionAssignment): boolean {
    return from?.subjectType != 'LOG_FOR' && (from?.assessmentTakers || []).findIndex((assessmentTaker: BulkSubscriptionAssignmentAssessmentTaker): boolean => assessmentTaker.email == from.login) >= 0;
  }

  getSubjectTypeText(): string {
    return this.isLogFor() ? 'Name' : 'Email'
  }

  isLogFor(): boolean {
    return this.subjectType?.value == 'LOG_FOR';
  }

  getListingName(): string {
    return this.subject.savedValue || 'New assignment';
  }

  getScoreLevel(from: BulkSubscriptionAssignment): ScoreReportingLevel {
    return from?.subjectScoreLevel || ScoreReportingLevel.NONE;
  }

  getStatusText(): string {
    switch(this.subscriptionStatus.value) {
      case BulkSubscriptionAssignmentStatus.CONFIRMED:
        return 'Confirmed';
      case BulkSubscriptionAssignmentStatus.PENDING:
        return 'Pending';
      case BulkSubscriptionAssignmentStatus.UNASSIGNED:
        return 'Unassigned'
      default:
        return null;
    }
  }

  getAssessmentTakerStrings(scoreName: string): string[] {
    return this.scoreInfo.get(scoreName)?.assessmentTakers || [];
  }

  getScoreTitleText(scoreName: string): string {
    return scoreName + ': ' + this.getAssessmentTakerStrings(scoreName).join(', ');
  }

  setScores(scores: CompassUserScoreInfo[]) {
    let scoreNames: string[] = [];
    let scoreInfo: Map<string, CompassUserScoreInfo> = new Map<string, CompassUserScoreInfo>();
    scores.forEach((score: CompassUserScoreInfo) => {
      scoreNames.push(score.score);
      scoreInfo.set(score.score, score);
    });
    scoreNames.sort();
    this.scoreNames = scoreNames;
    this.scoreInfo = scoreInfo;
  }

  isUnassigned(): boolean {
    return this.subscriptionStatus.value == BulkSubscriptionAssignmentStatus.UNASSIGNED;
  }

  isPending(): boolean {
    return this.subscriptionStatus.value == BulkSubscriptionAssignmentStatus.PENDING;
  }

  getSavedName(): string {
    return this.subject.savedValue;
  }

  beforeGroupChanged() {}

  onGroupChanged() {}

  onUpdateFrom(from: BulkSubscriptionAssignment) {
    this.bulkSubscriptionId = from ? from.bulkSubscriptionId : null;
    this.compassId = from ? from.compassId : null;
    this.subjectUserId = from ? from.subjectUserId : null;
    this.parent.scoreAssignment(this);
    this.subscriptionStatus.savedValue = from ? from.subscriptionStatus : BulkSubscriptionAssignmentStatus.PENDING;
    this.subscriptionStatus.reset();
    this.takesOwnAssessment.savedValue = this.getTakesOwnAssessment(from);
    this.subjectType.savedValue = this.getSubjectType(from);
    this.subject.savedValue = this.getSubject(from);
    this.subjectScoreLevel.savedValue = this.getScoreLevel(from);
    this.savedAssessmentTakers = this.getAssessmentTakers(from);
    this.assessmentTakers.forEach((taker: BulkSubscriptionAssignmentAssessmentTakerGroup, index: number) => taker.updateFrom(this.savedAssessmentTakers[index]));
    this.statusField?.update();
  }

  touchAll(value: boolean): void {
    this.statusField.touch(value);
    this.subjectType.touch(value);
    this.subject.touch(value);
    this.subjectScoreLevel.touch(value);
    this.assessmentTakers.forEach((taker: BulkSubscriptionAssignmentAssessmentTakerGroup) => taker.touchAll(value));
  }

  warnValidation(): void {
    this.parent.snackbar.open('Fix validation issues', 'Dismiss', {
      duration: 3000
    });
  }

  onReset(): void {
    this.subjectType.reset();
    this.subject.reset();
    this.subjectScoreLevel.reset();
    this.setAssessmentTakers(this.savedAssessmentTakers);
    this.assessmentTakers.forEach((taker: BulkSubscriptionAssignmentAssessmentTakerGroup) => taker.resetAll());
    this.statusField.reset();
    this.statusField?.update();
  }

  getSaved(): BulkSubscriptionAssignment {
    let assessmentTakers: BulkSubscriptionAssignmentAssessmentTaker[] = this.assessmentTakers.map((assessmentTaker: BulkSubscriptionAssignmentAssessmentTakerGroup): BulkSubscriptionAssignmentAssessmentTaker => assessmentTaker.getSaved());
    if(this.takesOwnAssessment.value)
      assessmentTakers.splice(0, 0, new BulkSubscriptionAssignmentAssessmentTaker({
        id: this.subjectUserId,
        email: this.subject.value,
        scoreReportingLevel: this.subjectScoreLevel.value,
        assignmentId: this.id
      }))
    return new BulkSubscriptionAssignment({
      id: this.id,
      compassId: this.compassId,
      bulkSubscriptionId: this.bulkSubscriptionId,
      subscriptionStatus: this.subscriptionStatus.value,
      login: this.isLogFor() ? null : this.subject.value,
      subject: this.isLogFor() ? this.subject.value : '',
      subjectType: this.assessmentTakers.length == 0 ? 'SELF' : this.subjectType.value,
      subjectScoreLevel: this.subjectScoreLevel.value,
      assessmentTakers: assessmentTakers
    });
  }

  async onSave(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return this.parent.store.dispatch(new SaveBulkSubscriptionAssignment(this.getSaved())).toPromise().then(() => true).catch((error: any) => {
      if(displayErrorIfPresent)
        this.parent.errorHandler.handle(error, this.getSavedName())
      return false;
    });
  }

  async onDeleteExisting(displayErrorIfPresent: boolean = true): Promise<boolean> {
    return Promise.resolve(false);
  }

  onDeleteNew(): void { }

  onDetach(): void {
    this.assessmentTakers.forEach((taker: BulkSubscriptionAssignmentAssessmentTakerGroup) => taker.destroy());
  }

  getAssessmentTakers(from?: BulkSubscriptionAssignment): BulkSubscriptionAssignmentAssessmentTaker[] {
    return (from?.assessmentTakers || []).filter((assessmentTaker: BulkSubscriptionAssignmentAssessmentTaker): boolean => assessmentTaker.email != from?.login);
  }

  addAssessmentTaker(from?: BulkSubscriptionAssignmentAssessmentTaker, shouldUpdate: boolean = true) {
    this.assessmentTakers.push(new BulkSubscriptionAssignmentAssessmentTakerGroup(from, this.assessmentTakersGroup.updators, this.assessmentTakerEmailDependency, this));
    if(shouldUpdate)
      this.statusField?.update();
  }

  removeAssessmentTaker(index: number, shouldUpdate: boolean = true) {
    if(index == null)
      return;
    this.assessmentTakers[index].destroy();
    this.assessmentTakers.splice(index, 1);
    if(shouldUpdate)
      this.statusField?.update();
  }

  compareTo(other: SavedAssignment): number {
    return this.getSaved().compareTo(other.getSaved());
  }

  static compare(a: SavedAssignment, b: SavedAssignment): number {
    return a.compareTo(b);
  }
}

export class BulkSubscriptionAssignmentAssessmentTakerGroup extends SavedGroupStatus {
  isNew: boolean;
  id: number;
  assignment: SavedAssignment;
  assessmentTakerEmailDependency: MapCountDependency<string>;
  email: SavedTextField;
  scoreLevel: SavedSelectField<ScoreReportingLevel>;

  constructor(from: BulkSubscriptionAssignmentAssessmentTaker, parentUpdators: CountUpdators, assessmentTakerEmailDependency: MapCountDependency<string>, assignment: SavedAssignment) {
    super(parentUpdators);
    this.isNew = from == null;
    this.id = from ? from.id : null;
    this.assignment = assignment;
    this.assessmentTakerEmailDependency = assessmentTakerEmailDependency;
    this.email = new SavedTextField(from ? from.email : null, null, this.updators, [this.assessmentTakerEmailDependency, this.assignment.subjectType, this.assignment.subject], [
      (field: SavedTextField): string => field.value == assignment.subject.value && assignment.subjectType?.value == 'TRUSTED_USER' ? 'Same as subject' : '',
      (field: SavedTextField): string => assessmentTakerEmailDependency.count(field.value) > 1 ? 'Duplicate' : ''
    ], null, (oldValue: string, newValue: string) => {
      if(oldValue == newValue)
        return;
      if(oldValue != null && newValue != null)
        assessmentTakerEmailDependency.replace(oldValue, newValue);
      else if(oldValue != null)
        assessmentTakerEmailDependency.subtract(oldValue);
      else if(newValue != null)
        assessmentTakerEmailDependency.add(newValue);
    }, true, null, 128);
    this.scoreLevel = new SavedSelectField(this.getScoreLevel(from), null, this.updators, null, null, true, null, null, new SelectOptions([
      { displayValue: 'None', value: ScoreReportingLevel.NONE },
      { displayValue: 'Summary', value: ScoreReportingLevel.SUMMARY },
      { displayValue: 'Component', value: ScoreReportingLevel.COMPONENT },
      { displayValue: 'Detailed', value: ScoreReportingLevel.DETAILED }
    ]));
    if(!this.isNew)
      this.touchAll(true);
  }

  getScoreLevel(from: BulkSubscriptionAssignmentAssessmentTaker): ScoreReportingLevel {
    return from?.scoreReportingLevel || ScoreReportingLevel.NONE;
  }

  destroy() {
    if(this.email.value != null)
      this.assessmentTakerEmailDependency.subtract(this.email.value);
    this.detach();
  }

  updateFrom(from: BulkSubscriptionAssignmentAssessmentTaker) {
    this.isNew = from == null;
    this.id = from ? from.id : null;
    this.email.savedValue = from ? from.email : null;
    this.scoreLevel.savedValue = this.getScoreLevel(from);
    if(!this.isNew)
      this.touchAll(true);
  }

  touchAll(value: boolean = true) {
    this.email.touch(value);
    this.scoreLevel.touch(value);
  }

  resetAll() {
    this.email.reset();
    this.scoreLevel.reset();
  }

  getSaved(): BulkSubscriptionAssignmentAssessmentTaker {
    return new BulkSubscriptionAssignmentAssessmentTaker({
      id: this.id,
      email: this.email.value,
      scoreReportingLevel: this.scoreLevel.value,
      assignmentId: this.assignment.id
    });
  }
}

interface CompassUserScoreInfo {
  score: string;
  assessmentTakers: string[];
}